import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Library,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  BookOpen,
  ArrowLeft,
  Settings,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useChaptersBySeriesId } from "../../hooks/useChapters";
import styles from "./BookPreview.module.css";

function BookPreview() {
  const [showLibrary, setShowLibrary] = useState(false);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [animating, setAnimating] = useState(false);
  const [fontSize, setFontSize] = useState(18);
  const [pageColor, setPageColor] = useState("#f8f5e9"); // Default cream color
  const [textColor, setTextColor] = useState("#333333"); // Default dark gray
  const [showSettings, setShowSettings] = useState(false);
  const [pages, setPages] = useState([]);
  const [showPageJump, setShowPageJump] = useState(false);
  const [jumpPageNumber, setJumpPageNumber] = useState("");

  const contentRef = useRef(null);
  const containerRef = useRef(null);

  // Get route parameters
  const { id, slug } = useParams();
  const location = useLocation();
  const seriesId = location.state?.seriesId || id;
  const navigate = useNavigate();

  // Get series data from hook
  const { series, chapters } = useChaptersBySeriesId(seriesId);

  // Colors for theme options
  const colorThemes = [
    { bg: "#f8f5e9", text: "#333333", name: "Cream" },
    { bg: "#ffffff", text: "#333333", name: "White" },
    { bg: "#f0f0f0", text: "#333333", name: "Light Gray" },
    { bg: "#1a1a1a", text: "#e0e0e0", name: "Dark" },
    { bg: "#2d2d2d", text: "#d4c7a5", name: "Sepia Dark" },
  ];

  // Helper function to format title for URL
  const formatTitleForUrl = useCallback((title) => {
    if (!title) return "";

    if (/[^\u0000-\u007F]/.test(title)) {
      return title;
    }

    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");
  }, []);

  // Define navigation functions
  const navigateBack = useCallback(() => {
    navigate(`/book-details/${chapters[0].ebook_slug}`);
  }, [navigate, chapters]);

  // Calculate pages based on content and container size
  const calculatePages = useCallback(() => {
    if (
      !chapters ||
      chapters.length === 0 ||
      currentChapterIndex >= chapters.length
    )
      return;

    const currentChapter = chapters[currentChapterIndex];
    if (!currentChapter?.ebook_content) return;

    // Parse the HTML content
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(
      currentChapter.ebook_content,
      "text/html"
    );
    const textContent = htmlDoc.body.textContent || "";

    // Estimate words per page (can be adjusted)
    const averageWordsPerPage = 250;
    const words = textContent.trim().split(/\s+/);
    const estimatedPages = Math.ceil(words.length / averageWordsPerPage);

    const contentPages = [];
    for (let i = 0; i < estimatedPages; i++) {
      const startIndex = i * averageWordsPerPage;
      const endIndex = Math.min(startIndex + averageWordsPerPage, words.length);
      const pageWords = words.slice(startIndex, endIndex);
      contentPages.push(pageWords.join(" "));
    }

    setPages(contentPages);
    setTotalPages(contentPages.length || 1);
    setCurrentPage(0); // Reset to first page when chapter changes
  }, [chapters, currentChapterIndex]);

  // Next page handler
  const nextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      setCurrentPage((prev) => prev + 1);
    } else if (currentChapterIndex < chapters.length - 1) {
      // Go to next chapter
      nextChapter();
    }
  }, [currentPage, totalPages, currentChapterIndex, chapters]);

  // Previous page handler
  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    } else if (currentChapterIndex > 0) {
      // Go to previous chapter
      prevChapter();
    }
  }, [currentPage, currentChapterIndex]);

  const nextChapter = useCallback(() => {
    if (!chapters || currentChapterIndex >= chapters.length - 1 || animating)
      return;

    setAnimating(true);
    const nextIndex = currentChapterIndex + 1;
    const nextChapterTitle = chapters[nextIndex].ebook_title;
    const formattedTitle = formatTitleForUrl(nextChapterTitle);

    navigate(`/book/${id}/episode/${formattedTitle}`, {
      state: { seriesId },
    });

    setCurrentChapterIndex(nextIndex);

    setTimeout(() => {
      setAnimating(false);
    }, 100);
  }, [
    chapters,
    currentChapterIndex,
    animating,
    navigate,
    id,
    seriesId,
    formatTitleForUrl,
  ]);

  const prevChapter = useCallback(() => {
    if (!chapters || currentChapterIndex <= 0 || animating) return;

    setAnimating(true);
    const prevIndex = currentChapterIndex - 1;
    const prevChapterTitle = chapters[prevIndex].ebook_title;
    const formattedTitle = formatTitleForUrl(prevChapterTitle);

    navigate(`/book/${id}/episode/${formattedTitle}`, {
      state: { seriesId },
    });

    setCurrentChapterIndex(prevIndex);

    setTimeout(() => {
      setAnimating(false);
    }, 100);
  }, [
    chapters,
    currentChapterIndex,
    animating,
    navigate,
    id,
    seriesId,
    formatTitleForUrl,
  ]);

  const selectChapter = useCallback(
    (index) => {
      if (!chapters || index === currentChapterIndex || animating) return;

      setAnimating(true);
      const selectedChapterTitle = chapters[index].ebook_title;
      const formattedTitle = formatTitleForUrl(selectedChapterTitle);

      navigate(`/book/${id}/episode/${formattedTitle}`, {
        state: { seriesId },
      });

      setCurrentChapterIndex(index);

      if (window.innerWidth < 768) {
        setShowLibrary(false);
      }

      setTimeout(() => {
        setAnimating(false);
      }, 100);
    },
    [
      chapters,
      currentChapterIndex,
      animating,
      navigate,
      id,
      seriesId,
      formatTitleForUrl,
    ]
  );

  // Toggle library sidebar
  const toggleLibrary = useCallback(() => {
    setShowLibrary((prev) => !prev);
  }, []);

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const applyColorTheme = (bg, text) => {
    setPageColor(bg);
    setTextColor(text);
  };

  const changeFontSize = (delta) => {
    const newSize = fontSize + delta;
    if (newSize >= 14 && newSize <= 24) {
      setFontSize(newSize);
    }
  };

  const handlePageJump = () => {
    const pageNum = parseInt(jumpPageNumber);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum - 1);
      setJumpPageNumber("");
      setShowPageJump(false);
    }
  };

  const handlePageJumpInputChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setJumpPageNumber(value);
    }
  };

  // Find the current chapter index based on the URL parameter
  useEffect(() => {
    if (chapters && chapters.length > 0 && slug) {
      const slugIndex = chapters.findIndex(
        (chapter) => chapter.ebook_slug === slug
      );

      let foundIndex = -1;
      if (slugIndex !== -1) {
        foundIndex = slugIndex;
      } else {
        const titleIndex = chapters.findIndex((chapter) => {
          const formattedTitle = formatTitleForUrl(chapter.ebook_title);
          return formattedTitle === slug;
        });

        if (titleIndex !== -1) {
          foundIndex = titleIndex;
        } else {
          foundIndex = 0;
        }
      }

      if (foundIndex !== currentChapterIndex) {
        setCurrentChapterIndex(foundIndex);
      }
    }
  }, [chapters, slug, formatTitleForUrl, currentChapterIndex]);

  // Calculate pages when content changes
  useEffect(() => {
    const timer = setTimeout(() => {
      calculatePages();
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [calculatePages, currentChapterIndex, fontSize]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        nextPage();
      } else if (e.key === "ArrowLeft") {
        prevPage();
      } else if (e.key === "Escape") {
        setShowSettings(false);
        setShowPageJump(false);
        setShowLibrary(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [nextPage, prevPage]);

  // Loading state
  if (!chapters || chapters.length === 0 || !series) {
    return (
      <div className={styles.loaderContainer}>
        <div className={styles.loader}></div>
        <p>Loading book content...</p>
      </div>
    );
  }

  // Make sure we have a valid currentChapterIndex
  const validIndex = Math.min(
    Math.max(0, currentChapterIndex),
    chapters.length - 1
  );
  const currentChapter = chapters[validIndex];

  if (!currentChapter) {
    return (
      <div className={styles.errorContainer}>
        <h2>Chapter not found</h2>
        <button onClick={navigateBack} className={styles.backButton}>
          <ArrowLeft size={20} /> Go Back
        </button>
      </div>
    );
  }

  return (
    <div
      className={styles.readerContainer}
      style={{ backgroundColor: pageColor }}
    >
      {/* Library sidebar */}
      <div
        className={`${styles.librarySidebar} ${
          showLibrary ? styles.active : ""
        }`}
        style={{ backgroundColor: pageColor, color: textColor }}
      >
        <div className={styles.libraryHeader}>
          <h2 style={{ color: textColor }}>Library</h2>
          <button
            className={styles.closeButton}
            onClick={toggleLibrary}
            style={{ color: textColor }}
          >
            <X size={24} />
          </button>
        </div>
        <div className={styles.chapterList}>
          <h3 style={{ color: textColor }}>{series.title}</h3>
          <p
            className={styles.author}
            style={{ color: textColor, opacity: 0.7 }}
          >
            by {currentChapter.ebook_author}
          </p>
          {chapters.map((chapter, index) => (
            <div
              key={chapter.id}
              className={`${styles.chapterItem} ${
                index === validIndex ? styles.active : ""
              }`}
              onClick={() => selectChapter(index)}
              style={{
                color: index === validIndex ? pageColor : textColor,
                backgroundColor:
                  index === validIndex ? textColor : "transparent",
              }}
            >
              <span className={styles.chapterNumber}>
                Chapter {chapter.chapter_number}
              </span>
              <span className={styles.chapterTitle}>{chapter.ebook_title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Header */}
      <div
        className={styles.header}
        style={{ backgroundColor: pageColor, color: textColor }}
      >
        <button
          onClick={toggleLibrary}
          className={styles.libraryButton}
          style={{ color: textColor }}
        >
          <Library size={20} />
        </button>
        <button
          onClick={() => navigate(-1)}
          className={styles.libraryButton}
          style={{ color: textColor }}
        >
          <ArrowLeft size={20} />
        </button>
        <div className={styles.bookInfo} style={{ color: textColor }}>
          <h1 style={{ color: textColor }}>{series.title}</h1>
          <p style={{ color: textColor, opacity: 0.7 }}>
            Chapter {currentChapter.chapter_number}:{" "}
            {currentChapter.ebook_title}
          </p>
        </div>
        <button
          onClick={toggleSettings}
          className={styles.settingsButton}
          style={{ color: textColor }}
        >
          <Settings size={20} />
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div
          className={styles.settingsPanel}
          style={{ backgroundColor: pageColor, color: textColor }}
        >
          <div className={styles.settingsSection}>
            <h3 style={{ color: textColor }}>Font Size</h3>
            <div className={styles.fontSizeControls}>
              <button
                onClick={() => changeFontSize(-1)}
                disabled={fontSize <= 14}
                style={{
                  borderColor: textColor,
                  color: textColor,
                  backgroundColor: "transparent",
                }}
              >
                A-
              </button>
              <span style={{ color: textColor }}>{fontSize}px</span>
              <button
                onClick={() => changeFontSize(1)}
                disabled={fontSize >= 24}
                style={{
                  borderColor: textColor,
                  color: textColor,
                  backgroundColor: "transparent",
                }}
              >
                A+
              </button>
            </div>
          </div>
          <div className={styles.settingsSection}>
            <h3 style={{ color: textColor }}>Theme</h3>
            <div className={styles.themeOptions}>
              {colorThemes.map((theme, index) => (
                <button
                  key={index}
                  className={`${styles.themeButton} ${
                    theme.bg === pageColor && theme.text === textColor
                      ? styles.activeTheme
                      : ""
                  }`}
                  style={{ backgroundColor: theme.bg, color: theme.text }}
                  onClick={() => applyColorTheme(theme.bg, theme.text)}
                >
                  {theme.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Page Jump Modal */}
      {showPageJump && (
        <div className={styles.pageJumpOverlay}>
          <div
            className={styles.pageJumpModal}
            style={{ backgroundColor: pageColor, color: textColor }}
          >
            <h3 style={{ color: textColor }}>Go to Page</h3>
            <div className={styles.pageJumpControls}>
              <input
                type="text"
                value={jumpPageNumber}
                onChange={handlePageJumpInputChange}
                placeholder={`1-${totalPages}`}
                className={styles.pageJumpInput}
                style={{
                  backgroundColor: "transparent",
                  color: textColor,
                  borderColor: textColor,
                }}
                onKeyPress={(e) => e.key === "Enter" && handlePageJump()}
                autoFocus
              />
              <button
                onClick={handlePageJump}
                className={styles.pageJumpButton}
                style={{
                  backgroundColor: textColor,
                  color: pageColor,
                }}
                disabled={
                  !jumpPageNumber ||
                  parseInt(jumpPageNumber) < 1 ||
                  parseInt(jumpPageNumber) > totalPages
                }
              >
                Go
              </button>
            </div>
            <button
              onClick={() => setShowPageJump(false)}
              className={styles.pageJumpCancel}
              style={{ color: textColor }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div
        className={styles.contentArea}
        ref={contentRef}
        style={{
          fontSize: `${fontSize}px`,
          color: textColor,
          backgroundColor: pageColor,
        }}
      >
        {pages.length > 0 ? (
          <div className={styles.pageContent}>
            {currentPage === 0 && (
              <div className={styles.bookHeader}>
                <h1 className={styles.bookTitle} style={{ color: textColor }}>
                  Chapter {currentChapter.chapter_number}
                </h1>
                <h2
                  className={styles.bookSubtitle}
                  style={{ color: textColor, opacity: 0.8 }}
                >
                  {currentChapter.ebook_title}
                </h2>
                <div
                  className={styles.titleSeparator}
                  style={{ backgroundColor: textColor, opacity: 0.3 }}
                ></div>
              </div>
            )}
            <p>{pages[currentPage]}</p>
          </div>
        ) : (
          <div
            className={styles.pageContent}
            dangerouslySetInnerHTML={{ __html: currentChapter.ebook_content }}
          />
        )}
      </div>

      {/* Pagination Controls */}
      <div
        className={styles.paginationControls}
        style={{ backgroundColor: pageColor, borderColor: `${textColor}20` }}
      >
        <button
          onClick={prevPage}
          disabled={currentPage === 0 && currentChapterIndex === 0}
          className={styles.pageButton}
          style={{ color: textColor }}
          aria-label="Previous page"
        >
          <ChevronLeft size={24} />
        </button>
        <div className={styles.pageInfo} style={{ color: textColor }}>
          <button
            onClick={() => setShowPageJump(true)}
            className={styles.pageInfoButton}
            style={{ color: textColor }}
          >
            Page {currentPage + 1} of {totalPages}
          </button>
        </div>
        <button
          onClick={nextPage}
          disabled={
            currentPage >= totalPages - 1 &&
            currentChapterIndex >= chapters.length - 1
          }
          className={styles.pageButton}
          style={{ color: textColor }}
          aria-label="Next page"
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
}

export default BookPreview;
